/**
 * The Morse learning engine (docs/MORSE.md §4): per-letter spaced repetition and
 * the planner that decides what to practise next. Pure — no clock, no storage,
 * no randomness it was not handed — so every rule here is testable as a table.
 *
 * The loop the household asked for (2026-10-13):
 *   1. a lesson's letters are **shown** (an intro card per letter, with the
 *      learner's own ezelsbruggetje);
 *   2. then **tested by ear** until each is remembered (MASTERY_BOX correct in a
 *      row);
 *   3. then **words** made only of known letters are played and typed, until
 *      each new letter has been heard in a few (WORD_HITS_NEEDED);
 *   4. then the next lesson unlocks — and the earlier letters keep coming back,
 *      on a Leitner schedule, more often after a miss.
 */
import { MORSE_LESSONS } from './code.js';
/** Minutes until the next review, per box. Box 0 is "again, now". */
export const BOX_INTERVALS_MIN = [0, 1, 10, 60, 24 * 60, 3 * 24 * 60, 7 * 24 * 60, 21 * 24 * 60];
export const MAX_BOX = BOX_INTERVALS_MIN.length - 1;
/** Correct in a row to count as remembered. */
export const MASTERY_BOX = 4;
/** Words each new letter must be heard in before the next lesson. */
export const WORD_HITS_NEEDED = 3;
/** Below this many available words a lesson skips its word phase — after E and
 *  T alone there is nothing worth typing. */
export const MIN_WORD_POOL = 4;
export function emptyProgress(letter) {
    return {
        letter,
        box: 0,
        due_at: null,
        correct: 0,
        wrong: 0,
        word_hits: 0,
        introduced_at: null,
        mastered_at: null,
        mnemonic: null,
    };
}
const addMinutes = (now, min) => new Date(now.getTime() + min * 60_000).toISOString();
/** The letter has been shown. Idempotent: the first introduction stands. */
export function introduce(p, now) {
    return p.introduced_at ? p : { ...p, introduced_at: now.toISOString(), due_at: now.toISOString() };
}
/** Record one recognition answer for `p.letter`. */
export function recordAnswer(p, correct, now) {
    const box = correct ? Math.min(MAX_BOX, p.box + 1) : 0;
    return {
        ...p,
        box,
        correct: p.correct + (correct ? 1 : 0),
        wrong: p.wrong + (correct ? 0 : 1),
        due_at: addMinutes(now, BOX_INTERVALS_MIN[box]),
        mastered_at: p.mastered_at ?? (box >= MASTERY_BOX ? now.toISOString() : null),
    };
}
/**
 * The letters a mistyped word got wrong — position by position, only when the
 * lengths agree (otherwise there is no telling which letter was misheard, and
 * blaming the wrong one teaches the schedule something false).
 */
export function wordMistakes(expected, typed) {
    const e = expected.toUpperCase();
    const t = typed.toUpperCase().trim();
    if (e.length !== t.length)
        return [];
    const wrong = new Set();
    for (let i = 0; i < e.length; i++)
        if (e[i] !== t[i])
            wrong.add(e[i]);
    return [...wrong];
}
/* ---------------- lessons ---------------- */
export function unlockedLetters(progress) {
    return MORSE_LESSONS.flat().filter((l) => progress[l]?.introduced_at);
}
/** The highest lesson with an introduced letter, or -1 before the first. */
export function currentLesson(progress) {
    for (let i = MORSE_LESSONS.length - 1; i >= 0; i--) {
        if (MORSE_LESSONS[i].some((l) => progress[l]?.introduced_at))
            return i;
    }
    return -1;
}
/** Words made only of `known`, containing at least one of `must` (if given),
 *  in the list's own (frequency) order. */
export function wordPool(words, known, must) {
    const k = new Set(known);
    return words.filter((w) => [...w].every((c) => k.has(c)) && (!must || [...w].some((c) => must.includes(c))));
}
export function lessonState(lesson, progress, words) {
    const letters = MORSE_LESSONS[lesson];
    if (!letters.some((l) => progress[l]?.introduced_at))
        return 'locked';
    if (!letters.every((l) => progress[l]?.introduced_at && progress[l]?.mastered_at))
        return 'letters';
    const pool = wordPool(words, unlockedLetters(progress), letters);
    if (pool.length < MIN_WORD_POOL)
        return 'done';
    return letters.every((l) => (progress[l]?.word_hits ?? 0) >= WORD_HITS_NEEDED) ? 'done' : 'words';
}
/** How many of a word pool's most frequent entries a pick draws from. */
const WORD_WINDOW = 300;
function pickWeighted(items, random) {
    const total = items.reduce((t, i) => t + i.weight, 0);
    if (total <= 0)
        return null;
    let r = random() * total;
    for (const i of items) {
        r -= i.weight;
        if (r < 0)
            return i.item;
    }
    return items[items.length - 1].item;
}
/** The most overdue introduced letter outside `exclude`, or null if none is due. */
function dueReview(progress, now, exclude) {
    let best = null;
    for (const l of unlockedLetters(progress)) {
        if (exclude.includes(l))
            continue;
        const due = progress[l]?.due_at;
        if (!due)
            continue;
        const at = Date.parse(due);
        if (at <= now.getTime() && (!best || at < best.at))
            best = { letter: l, at };
    }
    return best?.letter ?? null;
}
function pickLetter(input, focus, random) {
    const { progress, recent = [] } = input;
    const last = recent[recent.length - 1];
    const items = unlockedLetters(progress).map((l) => {
        const p = progress[l];
        // The lesson in hand dominates; a letter not yet remembered most of all.
        let weight = focus.includes(l) ? (p.mastered_at ? 3 : 6) : 1;
        // A weak box is asked more: the schedule's "comes back more often".
        weight *= 1 + (MAX_BOX - Math.min(p.box, MAX_BOX)) / MAX_BOX;
        // Three of one letter in a row is a pattern, not a test.
        if (l === last && recent[recent.length - 2] === l)
            weight = 0;
        return { item: l, weight };
    });
    return pickWeighted(items, random) ?? focus[0];
}
function pickWord(pool, recent, random) {
    const fresh = pool.slice(0, WORD_WINDOW).filter((w) => !recent.includes(w));
    const from = fresh.length ? fresh : pool.slice(0, WORD_WINDOW);
    if (!from.length)
        return null;
    return from[Math.floor(random() * from.length)];
}
export function nextStep(input) {
    const { progress, words, now, recent = [] } = input;
    const random = input.random ?? Math.random;
    const lesson = currentLesson(progress);
    if (lesson === -1)
        return { kind: 'intro', letter: MORSE_LESSONS[0][0] };
    const letters = MORSE_LESSONS[lesson];
    const unseen = letters.find((l) => !progress[l]?.introduced_at);
    if (unseen)
        return { kind: 'intro', letter: unseen };
    const state = lessonState(lesson, progress, words);
    const known = unlockedLetters(progress);
    if (state === 'letters') {
        // An earlier letter whose review is due gets a turn now and then, so the
        // new lesson never quietly costs the old ones.
        const review = random() < 0.25 ? dueReview(progress, now, letters) : null;
        return { kind: 'letter', letter: review ?? pickLetter(input, letters, random) };
    }
    if (state === 'words') {
        // Words that still need one of the letters short of its hits.
        const needy = letters.filter((l) => (progress[l]?.word_hits ?? 0) < WORD_HITS_NEEDED);
        const word = random() < 0.8 ? pickWord(wordPool(words, known, needy), recent, random) : null;
        return word ? { kind: 'word', word } : { kind: 'letter', letter: dueReview(progress, now, []) ?? pickLetter(input, letters, random) };
    }
    const next = MORSE_LESSONS[lesson + 1];
    if (next)
        return { kind: 'intro', letter: next[0] };
    // The whole alphabet is in: keep it alive — due letters first, words often.
    const due = dueReview(progress, now, []);
    if (due && random() < 0.5)
        return { kind: 'letter', letter: due };
    const word = pickWord(wordPool(words, known), recent, random);
    return word ? { kind: 'word', word } : { kind: 'letter', letter: pickLetter(input, [], random) };
}
/* ---------------- identity ---------------- */
/** 32-bit FNV-1a with a seed. */
function fnv(s, seed) {
    let h = (0x811c9dc5 ^ seed) >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
/**
 * The row id for one learner's letter, **derived, not random**. Two phones that
 * each meet the letter E for the first time while offline must write the same
 * row — a random id would make two E rows, and LWW cannot merge rows it does not
 * know are one. Shaped as a v4 UUID so it passes the same validation as any id.
 */
export function morseLetterId(userId, letter) {
    const key = `morse:${userId}:${letter.toUpperCase()}`;
    const hex = [1, 2, 3, 4].map((seed) => fnv(key, seed).toString(16).padStart(8, '0')).join('');
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        '4' + hex.slice(13, 16),
        ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20),
        hex.slice(20, 32),
    ].join('-');
}
//# sourceMappingURL=progress.js.map